import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import Portfolio from "@/components/Portfolio";
import About from "@/components/About";
import Process from "@/components/Process";
import LocalBusiness from "@/components/LocalBusiness";
import Testimonials from "@/components/Testimonials";
import FinalCTA from "@/components/FinalCTA";
import {
  problemPaths,
  projectPlans,
  recurringPlans,
  techPartnerOffer,
} from "@/lib/commercial-content";
import { getProjects } from "@/lib/portfolio-store";

const ProblemPathsPreview = () => (
  <section className="bg-white py-20 md:py-24">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 vc-reveal">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
            Problemas que resolvemos
          </p>
          <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
            Tecnologia para resolver problemas reais.
          </h2>
        </div>
        <Link
          to="/solucoes"
          className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-[#0a0a0a] hover:text-[#ff6b35] transition-colors"
        >
          <span>Ver soluções</span>
          <ArrowRight size={16} className="shrink-0" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {problemPaths.map((path) => {
          const Icon = path.icon;
          return (
            <article key={path.title} className="vc-reveal brutal-card bg-[#ff6b35] p-6 min-h-[250px] flex flex-col">
              <div className="bg-[#0a0a0a] w-14 h-14 flex items-center justify-center brutal-border mb-6">
                <Icon size={24} className="text-[#ff6b35]" />
              </div>
              <h3 className="font-display text-[#0a0a0a] text-3xl uppercase leading-none">
                {path.title}
              </h3>
              <p className="mt-4 text-[#0a0a0a]/75 leading-snug">{path.desc}</p>
              <p className="mt-5 font-mono text-xs uppercase tracking-widest text-white">
                {path.solution}
              </p>
              <Link
                to={path.href}
                className="mt-auto pt-6 inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-[#0a0a0a] hover:text-white transition-colors"
              >
                <span>{path.cta}</span>
                <ArrowRight size={16} className="shrink-0" />
              </Link>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

const PlansPreview = () => (
  <section className="bg-[#0a0a0a] py-20 md:py-28 relative overflow-hidden">
    <div className="absolute inset-0 dot-pattern opacity-10 pointer-events-none" />
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 vc-reveal">
        <div>
          <div className="inline-block bg-[#ff6b35] text-[#0a0a0a] px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-4">
            Planos
          </div>
          <h2 className="font-display text-white text-4xl sm:text-6xl uppercase leading-none">
            Investimento claro
          </h2>
        </div>
        <Link to="/planos" className="btn-brutal btn-brutal-orange inline-flex">
          <span>Ver planos</span>
          <ArrowRight size={18} className="shrink-0" />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.8fr)] gap-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {projectPlans.slice(0, 3).map((plan) => (
            <article key={plan.name} className="vc-reveal brutal-card bg-white p-6 min-h-[300px] flex flex-col">
              <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
                {plan.category}
              </p>
              <h3 className="font-display text-[#0a0a0a] text-3xl uppercase leading-none">
                {plan.name}
              </h3>
              <p className="mt-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                {plan.price}
              </p>
              <p className="mt-5 text-[#0a0a0a]/75 leading-snug">{plan.desc}</p>
            </article>
          ))}
        </div>

        <aside className="vc-reveal brutal-card bg-[#ff6b35] p-6 flex flex-col justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-white mb-3">
              Suporte recorrente
            </p>
            <h3 className="font-display text-[#0a0a0a] text-4xl uppercase leading-none">
              Care, Business, Growth
            </h3>
            <div className="mt-6 space-y-3">
              {recurringPlans.map((plan) => (
                <div key={plan.name} className="flex items-center justify-between gap-4 border-t-2 border-[#0a0a0a] pt-3">
                  <span className="font-display text-[#0a0a0a] text-2xl uppercase leading-none">
                    {plan.name}
                  </span>
                  <span className="font-mono text-[0.68rem] uppercase tracking-widest text-white text-right">
                    {plan.price}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Link
            to="/contacto"
            className="mt-8 inline-flex items-center gap-2 font-mono uppercase text-xs tracking-widest text-[#0a0a0a] hover:text-white transition-colors"
          >
            <span>Pedir proposta</span>
            <ArrowRight size={16} className="shrink-0" />
          </Link>
        </aside>
      </div>
    </div>
  </section>
);

const TechPartnerPreview = () => (
  <section className="bg-[#ff6b35] py-20 md:py-24">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 lg:gap-10 items-stretch">
        <div className="vc-reveal brutal-card bg-[#0a0a0a] p-7 sm:p-9 flex flex-col justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-4">
              Tech Partner
            </p>
            <h2 className="font-display text-white text-[2.25rem] min-[380px]:text-5xl sm:text-6xl uppercase leading-none">
              Uma equipa técnica sem contratar equipa interna
            </h2>
            <p className="mt-6 max-w-3xl text-white/75 text-base sm:text-lg leading-snug">
              {techPartnerOffer.summary}
            </p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/tech-partner" className="btn-brutal btn-brutal-orange justify-center">
              <span>Ver Tech Partner</span>
              <ArrowRight size={18} className="shrink-0" />
            </Link>
            <Link to="/contacto" className="btn-brutal justify-center">
              Falar connosco
            </Link>
          </div>
        </div>

        <aside className="vc-reveal brutal-card bg-white p-7 sm:p-8">
          <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
            Desde
          </p>
          <p className="font-display text-[#0a0a0a] text-5xl uppercase leading-none">
            {techPartnerOffer.price}
          </p>
          <ul className="mt-7 space-y-3">
            {techPartnerOffer.includes.slice(0, 5).map((item) => (
              <li key={item} className="flex gap-3 text-[#0a0a0a]/80 leading-snug">
                <span className="mt-2 h-2 w-2 bg-[#ff6b35] brutal-border shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  useEffect(() => {
    getProjects();

    try {
      const params = new URLSearchParams(window.location.search);
      const source = params.get("utm_source") || "direct";
      const medium = params.get("utm_medium") || "direct";
      const campaign = params.get("utm_campaign") || "direct";
      sessionStorage.setItem(
        "lead_source",
        JSON.stringify({ source, medium, campaign, timestamp: new Date().toISOString() })
      );
    } catch {
      // Storage may be unavailable; page animation setup should still run.
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("in");
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".vc-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main id="main-content">
      <Hero />
      <Services />
      <ProblemPathsPreview />
      <PlansPreview />
      <TechPartnerPreview />
      <LocalBusiness />
      <About />
      <Process />
      <Portfolio />
      <Testimonials />
      <FinalCTA />
    </main>
  );
};

export default HomePage;
